import React, { useState, useEffect } from "react";
import {
  List,
  Table,
  Tag,
  Space,
  Input,
  Select,
  Typography,
  Card,
  Button,
  Tooltip,
  Modal,
} from "antd";
import { 
  FilterOutlined, 
  ReloadOutlined, 
  SearchOutlined, 
  InfoCircleOutlined,
  ExclamationCircleOutlined 
} from "@ant-design/icons";
import axios from "axios";
import "../../Services/bridgeApiKey";

const { Title, Text } = Typography;
const { Option } = Select;

export const BridgeLogs: React.FC = () => {
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    level: "",
    sku: "",
    jobId: "",
  });

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_BRIDGE_URL}/products/logs`, {
        params: {
          ...filters,
          limit: 200,
        },
      });
      setLogs(response.data);
    } catch (error) {
      console.error("Failed to fetch logs", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [filters]);

  const showDetails = (log: any) => {
    Modal.info({
      title: `Log Details - ${log.sku || "N/A"}`,
      width: 800,
      content: (
        <pre style={{ maxHeight: "500px", overflow: "auto", background: "#f5f5f5", padding: "10px" }}>
          {JSON.stringify(log.details || log.message, null, 2)}
        </pre>
      ),
    });
  };

  const columns = [
    {
      title: "Timestamp",
      dataIndex: "timestamp",
      key: "timestamp",
      render: (t: string) => new Date(t).toLocaleString(),
      width: 180,
    },
    {
      title: "Level",
      dataIndex: "level",
      key: "level",
      width: 100,
      render: (level: string) => (
        <Tag color={level === "error" ? "red" : level === "warn" ? "orange" : "blue"}>
          {level.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Job ID",
      dataIndex: "jobId",
      key: "jobId",
      width: 150,
      render: (id: string) => <Text copyable>{id}</Text>,
    },
    {
      title: "SKU",
      dataIndex: "sku",
      key: "sku",
      width: 120,
    },
    {
      title: "Message",
      dataIndex: "message",
      key: "message",
      render: (msg: string, record: any) => (
        <Space>
          <Text ellipsis={{ tooltip: msg }} style={{ maxWidth: 300 }}>{msg}</Text>
          {record.details && (
            <Tooltip title="View full details">
              <InfoCircleOutlined 
                style={{ color: "#1890ff", cursor: "pointer" }} 
                onClick={() => showDetails(record)} 
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: "Context",
      dataIndex: "context",
      key: "context",
      width: 150,
    },
  ];

  return (
    <Card>
      <div style={{ marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Title level={4} style={{ margin: 0 }}>Bridge Sync Logs</Title>
        <Space>
          <Select
            placeholder="Level"
            allowClear
            style={{ width: 120 }}
            onChange={(v) => setFilters(prev => ({ ...prev, level: v }))}
          >
            <Option value="error">Error</Option>
            <Option value="warn">Warn</Option>
            <Option value="info">Info</Option>
          </Select>
          <Input
            placeholder="SKU"
            prefix={<SearchOutlined />}
            style={{ width: 150 }}
            onChange={(e) => setFilters(prev => ({ ...prev, sku: e.target.value }))}
          />
          <Input
            placeholder="Job ID"
            style={{ width: 150 }}
            onChange={(e) => setFilters(prev => ({ ...prev, jobId: e.target.value }))}
          />
          <Button icon={<ReloadOutlined />} onClick={fetchLogs} loading={loading} />
          <Button 
            danger 
            icon={<ExclamationCircleOutlined />} 
            onClick={() => setFilters(prev => ({ ...prev, level: "error" }))}
          >
            Errors Only
          </Button>
        </Space>
      </div>

      <Table
        dataSource={logs}
        columns={columns}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={{ pageSize: 20 }}
      />
    </Card>
  );
};
